import React, { useState } from 'react';
import { notification } from 'antd';
import passwordService from '../services/passwordService';

/**
 * Hook to handle user password updates
 */
const usePasswordUpdate = () => {
  const [loading, setLoading] = useState(false);
  
  /**
   * Update user password
   * @param {number|string} userId - User ID
   * @param {Object} passwordData - Password data containing current_password, password, and password_confirmation
   * @returns {Promise<boolean>} Success status
   */
  const updatePassword = async (userId, passwordData) => {
    try {
      setLoading(true);
      
      // Make sure we have all required fields
      if (!passwordData.current_password || !passwordData.password || !passwordData.password_confirmation) {
        throw new Error('Missing required password fields');
      }
      
      // Ensure password and confirmation match
      if (passwordData.password !== passwordData.password_confirmation) {
        throw new Error('Password and confirmation do not match');
      }
      
      // Call the API using the dedicated password service
      const response = await passwordService.updatePassword(userId, passwordData);
      
      notification.success({
        message: 'Password Updated',
        description: 'Your password has been changed successfully.'
      });
      
      return true;
    } catch (error) {
      let errorMessage = 'Failed to update password';
      
      // Extract error message from API response if available
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      notification.error({
        message: 'Password Update Failed',
        description: errorMessage
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    loading,
    updatePassword
  };
};

export default usePasswordUpdate;